import { Button } from '@abyss/web/ui/Button';
import { Layout } from '@abyss/web/ui/Layout';
import { Modal } from '@abyss/web/ui/Modal';
import find from 'lodash/find';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Disposition } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { useSaveOfferDisposition } from '../../../../hooks/useCeOffers';
import { useLagoon } from '../../../../hooks/useLagoon';
import {
  getCurrentMember,
  getEnterpriseId,
} from '../../../../utils/user.utils';

interface ProgramPromotionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOfferName: string | null;
  programPromotions: any;
}

export const ProgramPromotionModal: React.FC<ProgramPromotionModalProps> = ({
  isOpen,
  onClose,
  selectedOfferName,
  programPromotions,
}) => {
  const { t } = useTranslation();
  const data = useLagoon('ui-messaging')();
  const modalContent = find(data, {
    key: ConstantsLagoon.PROGRAM_PROMOTION.PROGRAM_PROMOTION_MODAL_CONTENT,
  });
  const [, saveOfferDisposition] = useSaveOfferDisposition({});
  const currentMember = getCurrentMember();

  const handleDecline = () => {
    if (selectedOfferName) {
      const offer = programPromotions.find(
        (o) => o.offerName === selectedOfferName
      );
      if (offer?.offerToken) {
        const enterpriseId = getEnterpriseId(currentMember);

        if (enterpriseId) {
          saveOfferDisposition({
            variables: {
              enterpriseId,
              disposition: Disposition.DECLINED,
              offerToken: offer.offerToken,
            },
          });
        }
      }
    }
  };

  return (
    <Modal
      css={{
        'abyss-modal-content-container': {
          width: '572px',
        },
      }}
      data-testid="program-promotion-modal"
      isOpen={isOpen}
      onClose={() => {
        handleDecline();
        adobeLinkTrackEvent({
          name: 'close',
          location: `modal:continue to site`,
          type: 'internal',
        });
        onClose();
      }}
      title={modalContent?.title}
    >
      <Modal.Section data-testid="program-promotion-modal-message">
        {modalContent?.message}
      </Modal.Section>
      <Modal.Section>
        <Layout.Group alignLayout="right">
          <Button
            data-testid="cancel-button"
            onClick={() => {
              handleDecline();
              adobeLinkTrackEvent({
                name: 'cancel',
                location: `modal:continue to site`,
                type: 'internal',
              });
              onClose();
            }}
            variant="outline"
          >
            {t('THINGS_TO_CONSIDER_MODAL.CANCEL_BUTTON')}
          </Button>
          <Button
            data-testid="continue-button"
            onClick={() => {
              if (selectedOfferName) {
                const offer = programPromotions.find(
                  (o) => o.offerName === selectedOfferName
                );
                if (offer?.cta?.ctaValue) {
                  const enterpriseId = getEnterpriseId(currentMember);
                  if (offer?.offerToken && enterpriseId) {
                    saveOfferDisposition({
                      variables: {
                        enterpriseId,
                        disposition: Disposition.ACCEPTED,
                        offerToken: offer.offerToken,
                      },
                    });
                  }
                  window.open(offer.cta.ctaValue, '_blank');
                  adobeLinkTrackEvent({
                    name: 'continue',
                    location: `modal:continue to site`,
                    type: 'external',
                    destinationUrl: offer.cta.ctaValue,
                  });
                }
              }
              onClose();
            }}
          >
            {t('THINGS_TO_CONSIDER_MODAL.CONTINUE_BUTTON')}
          </Button>
        </Layout.Group>
      </Modal.Section>
    </Modal>
  );
};
